/**
 * theme.js — styled-components theme, aligned to the Vikar Experten design system.
 *
 * CHANGED in 1.17.0-dev.1. Previously: "Design system inspired by
 * andreasagro.com" — a blue (#0055A4) / orange (#EF7B45) Montserrat + Open Sans
 * palette that had nothing to do with the company's fifteen-year-old brand.
 *
 * Every key that existed before still exists, with the same shape, so no
 * consumer had to change. Only the VALUES moved onto the brand. Where a value
 * is a deliberate app-only modernisation rather than a website value, the
 * comment says so.
 *
 * The same numbers live in styles/ve-tokens.css as CSS custom properties, for
 * the parts of the app that are styled with plain .css files. Keep the two in
 * sync — ve-tokens.css is the canonical list.
 */

export const theme = {
  colors: {
    // --- Primary ACTION: blue. -------------------------------------------------
    // A deliberate departure from the website, agreed with the client. The site
    // has one action colour (red), which cannot work in a tool where a customer
    // approves or rejects a worker's week: red has to mean "careful".
    //     primary = do it (log in, save, send)
    //     accent  = approve
    //     error   = reject / destructive / warning
    // Blue is derived in oklch from the brand's own #2c5364 filter-bar slate,
    // pushed up in chroma until it carries a button fill. Not the old #0055A4.
    // Brand red keeps the masthead, the logo ground, the splitter and chart
    // fills — see colors.brandRed below.
    primary: '#17627f',        // was #ec1f27 (and #0055A4 before the re-skin)
    primaryDark: '#114f67',    // action hover
    primaryLight: '#eaf2f7',   // pale action plate

    // --- The brand red, still the identity colour, no longer an action. -------
    brandRed: '#ec1f27',       // masthead plate, logo ground, splitter, charts
    brandRedDeep: '#970224',

    // --- Secondary: the deep-red heritage plate ------------------------------
    secondary: '#970224',
    secondaryDark: '#6d0019',
    secondaryLight: '#9b0225',

    // --- Accent: brand green = APPROVE ---------------------------------------
    accent: '#adcb53',         // approve fill, with ink text
    accentDark: '#7d9a34',     // green text on white
    accentLight: '#9cbb45',    // approve hover

    // --- Neutrals ------------------------------------------------------------
    dark: '#19242e',           // was #2C3E50 — the footer navy, our ink
    medium: '#505050',         // was #5D6D7E
    light: '#eeeeee',          // was #ECF0F1
    lightAlt: '#f7f7f7',       // was #F8F9FA

    text: '#333333',           // was #2C3E50 — the site's body copy colour
    textSecondary: '#505050',  // was #5D6D7E
    textTertiary: '#a4a4a4',   // was #95A5A6 — the site's muted grey
    background: '#ffffff',
    backgroundLight: '#f7f7f7',
    danger: '#c3251d',         // was #E74C3C

    // --- Feedback. Red is reserved for reject / destructive / error, so it no
    //     longer collides with the primary action. -----------------------------
    success: '#2db69e',        // was #2ECC71 — the source's one status colour
    warning: '#eea82f',        // was #F1C40F — legacy alternate accent
    error: '#c3251d',          // was #E74C3C — the deep brand red
    info: '#2c5364',           // was #3498DB — the filter-bar slate

    white: '#ffffff',
    offWhite: '#f7f7f7',       // was #F7F9FA
    black: '#050504',          // was #1A1A1A

    border: 'rgba(0, 0, 0, 0.125)', // was #E5E8E8 — the brand never uses a
                                    // solid grey border, only a translucent
                                    // hairline

    // --- ADDED: the raw brand scale, for the few places that need an exact
    //     step rather than a semantic name. -----------------------------------
    brand: {
      red050: '#fdecec', red100: '#f9c9cb', red500: '#ec1f27',
      red600: '#c3251d', red700: '#9b0225', red800: '#970224', red900: '#6d0019',
      blue050: '#eaf2f7', blue500: '#17627f', blue600: '#114f67',
      green300: '#a0da67', green400: '#a4cf63', green500: '#adcb53',
      green600: '#9cbb45', green700: '#7d9a34',
      ink900: '#050504', ink800: '#19242e', ink700: '#333333', ink600: '#505050',
      ink500: '#a4a4a4', ink400: '#777777', ink200: '#eeeeee', ink100: '#f7f7f7'
    },
    hairline: 'rgba(0, 0, 0, 0.1)',
    hairlineInverse: 'rgba(255, 255, 255, 0.2)',
    statusPlate: {
      success: '#eaf7f4', successInk: '#1d8e7c',
      warning: '#fdf4e6', warningInk: '#9a6a10',
      danger: '#fdecec', dangerInk: '#c3251d'
    }
  },

  typography: {
    // Roboto, and only Roboto. The website force-sets it on every element.
    fontFamily: {
      primary: "'Roboto', Helvetica, Arial, sans-serif",   // was Montserrat
      secondary: "'Roboto', Helvetica, Arial, sans-serif", // was Open Sans
      heading: "'Roboto', Helvetica, Arial, sans-serif",
      body: "'Roboto', Helvetica, Arial, sans-serif"
    },

    fontWeight: {
      light: 300,     // body copy — the brand's most recognisable type trait
      regular: 400,   // dense UI: table cells, nav items
      medium: 500,
      semiBold: 700,  // CHANGED 600 -> 700: the brand has no semibold. Every
                      // heading that asked for semiBold is now properly bold.
      bold: 700,
      black: 900      // ADDED: statistics / counters
    },

    // Sizes as authored on the site. Do NOT snap these to a 4px grid.
    fontSize: {
      xs: '13px',    // was 12px
      sm: '14px',    // was 14px
      base: '16px',  // was 16px — form controls, card body
      md: '18px',    // was 18px
      lg: '20px',    // was 20px — the site's default body size
      xl: '22px',    // was 24px — h3
      '2xl': '24px', // was 28px — h2 / section headings
      '3xl': '28px', // was 32px
      '4xl': '30px', // was 40px
      '5xl': '36px', // was 48px — h1
      '6xl': '60px'  // was 64px — counters
    },

    lineHeight: {
      none: 1,
      tight: 1.2,     // headings
      snug: 1.4,      // h4/h5, marker lists
      normal: 1.625,  // body — was 1.5
      relaxed: 1.8,   // lead paragraphs — was 1.625
      loose: 2
    },

    // The brand resets tracking to 0 everywhere and uses it in exactly one
    // place: 1.4px on a CTA label. The negative values below are therefore
    // flattened to 0 rather than removed, so old call sites stop tightening.
    letterSpacing: {
      tighter: '0',   // was -0.05em
      tight: '0',     // was -0.025em
      normal: '0',
      wide: '0.5px',  // was 0.025em
      wider: '1px',   // was 0.05em
      widest: '1.4px',// was 0.1em
      cta: '1.4px'    // ADDED: the one sanctioned use
    }
  },

  // Spacing moved onto the brand's stops: 4 · 8 · 10 · 15 · 16 · 20 · 25 · 30 ·
  // 40 · 50 · 60 · 80. Keys are unchanged, so existing call sites keep working;
  // the resulting rhythm is a little tighter than the old 4px-multiples scale.
  spacing: {
    '0': '0',
    '1': '4px',
    '2': '8px',
    '3': '10px',   // was 12px
    '4': '15px',   // was 16px
    '5': '20px',
    '6': '25px',   // was 24px
    '8': '30px',   // was 32px
    '10': '40px',
    '12': '50px',  // was 48px
    '16': '60px',  // was 64px
    '20': '80px',
    '24': '100px', // was 96px
    '32': '120px',
    '40': '160px',
    '48': '200px',
    '56': '240px',
    '64': '280px'
  },

  borders: {
    none: 'none',
    thin: '1px solid',
    medium: '2px solid',
    thick: '4px solid',
    // Radii — the client's APPROVED values, signed off 2026-07-28. The website is
    // square (two rules force border-radius:0); these are the design system's
    // sanctioned modernisation for app surfaces, small enough that buttons and
    // plates still read as square at a glance.
    //   md 4px      dense controls: inputs, selects, chips (~25 files use it)
    //   lg 6px      CARDS, panels, buttons, badges (~12 files use lg for card
    //               containers, which is what makes the value stick app-wide)
    //   chrome 3px  sidebar rail, top bar, auth masthead — see components.chrome
    //   pill        status plates and meter tracks
    radius: {
      none: '0',
      sm: '4px',       // inputs, selects, chips
      md: '4px',       // dense controls (unchanged from the old theme)
      content: '6px',  // ADDED: the approved card / button / plate radius
      lg: '6px',       // was 8px, then 0 — cards and panels
      xl: '6px',       // was 16px
      chrome: '3px',   // ADDED: sidebar, top bar, masthead
      full: '50%',
      pill: '999px',
      square: '0'      // explicit, where something must stay hard-cornered
    }
  },

  // The website has no card elevation at all (its only shadows are a hard
  // `0 0 5px black` and an inset press). Those are replaced by a layered scale
  // so app surfaces can stack. Cards still ship flat — see components.card.
  shadows: {
    none: 'none',
    xs: '0 1px 2px rgba(5, 5, 4, 0.06)',
    sm: '0 1px 3px rgba(5, 5, 4, 0.09), 0 1px 2px rgba(5, 5, 4, 0.05)',
    md: '0 4px 12px rgba(5, 5, 4, 0.10)',
    lg: '0 12px 28px rgba(5, 5, 4, 0.14)',
    xl: '0 12px 28px rgba(5, 5, 4, 0.14)',
    '2xl': '0 12px 28px rgba(5, 5, 4, 0.14)',
    inner: 'inset 0 2px 4px rgba(5, 5, 4, 0.22)',
    press: 'inset 0 2px 4px rgba(5, 5, 4, 0.22)',
    // The focus ring follows the action colour, so a focused reject button never
    // flashes the same halo as a focused save button.
    focus: '0 0 0 3px rgba(23, 98, 127, 0.35)',       // ADDED
    focusDanger: '0 0 0 3px rgba(195, 37, 29, 0.3)',  // ADDED
    focusAccent: '0 0 0 3px rgba(173, 203, 83, 0.45)' // ADDED
  },

  // 100ms on controls (genesis sets `transition: all .1s ease-in-out` on every
  // interactive element), 200ms on opacity, 350ms on media, 500ms on the CTA
  // arrow with an overshoot curve — the brand's one piece of personality.
  transitions: {
    default: 'all 0.1s ease-in-out', // was all 0.3s ease
    fast: 'all 0.1s ease-in-out',
    slow: 'all 0.35s ease',
    opacity: 'opacity 0.2s ease-in-out',
    media: 'all 0.35s ease',
    cta: 'all 0.5s cubic-bezier(0.64, 0.01, 0.3, 1.46) 0.2s'
  },

  zIndex: { '0': 0, '10': 10, '20': 20, '30': 30, '40': 40, '50': 50, '100': 100, 'auto': 'auto' },

  breakpoints: { mobile: '576px', tablet: '768px', desktop: '1024px' },

  // Widths are the brand's: reading measure 600, sidebar 360, content beside a
  // sidebar 660, main content 880, tile grid 1140, container 1280.
  grid: {
    containerWidth: {
      sm: '600px',   // was 640px  — reading measure
      md: '660px',   // was 768px  — content beside a sidebar
      lg: '880px',   // was 1024px — main content
      xl: '1140px',  // was 1280px — grid
      xxl: '1280px'  // was 1440px — container / header
    },
    columns: 12,
    gap: '30px' // was 2rem — the brand's Bootstrap-4 gutter
  },

  components: {
    // blue does, green approves, red rejects. Square, uppercase, 1.4px tracking.
    // Hover darkens the button's OWN fill — green can no longer be the universal
    // hover, because green now carries meaning.
    button: {
      primary: {
        backgroundColor: '#17627f',
        color: '#ffffff',
        hoverBackgroundColor: '#114f67',
        hoverColor: '#ffffff',
        borderRadius: '6px'
      },
      // approve
      accent: {
        backgroundColor: '#adcb53',
        color: '#19242e',
        hoverBackgroundColor: '#9cbb45',
        hoverColor: '#19242e',
        borderRadius: '6px'
      },
      // reject / destructive: outline until pressed, then it fills
      danger: {
        backgroundColor: 'transparent',
        color: '#c3251d',
        borderColor: '#c3251d',
        hoverBackgroundColor: '#c3251d',
        hoverColor: '#ffffff',
        borderRadius: '6px'
      },
      secondary: {
        backgroundColor: '#970224',
        color: '#ffffff',
        hoverBackgroundColor: '#6d0019',
        hoverColor: '#ffffff',
        borderRadius: '6px'
      },
      outline: {
        backgroundColor: 'transparent',
        color: '#17627f',
        borderColor: '#17627f',
        hoverBackgroundColor: '#17627f',
        hoverColor: '#ffffff',
        borderRadius: '6px'
      },
      ghost: {
        backgroundColor: 'transparent',
        color: '#17627f',
        hoverBackgroundColor: 'rgba(0, 0, 0, 0.05)',
        hoverColor: '#114f67',
        borderRadius: '6px'
      }
    },

    // The app chrome: sidebar rail, top bar, auth masthead. A 3px radius with a
    // 5px inset, so the rail reads as a plate rather than a bleed. Both values
    // are the client's approved settings.
    chrome: {
      borderRadius: '3px',
      inset: '5px'
    },

    // Status plates are pills at the client's request.
    badge: {
      borderRadius: '999px'
    },

    // White, 1px translucent hairline, 6px corners and the faintest app shadow
    // ("blød") — the client's approved values. The website itself has no card
    // elevation at all, so this is the one place the app is softer than the site.
    card: {
      backgroundColor: '#ffffff',
      border: '1px solid rgba(0, 0, 0, 0.1)',    // was none
      borderRadius: '6px',                       // was 0.5rem, then 0
      padding: '25px',
      boxShadow: '0 1px 2px rgba(5, 5, 4, 0.06)' // --shadow-xs
    },

    form: {
      input: {
        backgroundColor: '#ffffff',
        borderRadius: '4px',                    // dense-control modernisation
        borderColor: 'rgba(0, 0, 0, 0.125)',
        padding: '11px 15px',
        fontSize: '16px',                       // 16px also stops iOS zoom
        focusBorderColor: '#17627f',
        focusRing: '0 0 0 3px rgba(23, 98, 127, 0.35)'
      },
      label: {
        color: '#19242e',
        fontSize: '14px',
        fontWeight: 700, // was 600 — the brand has no semibold
        marginBottom: '8px'
      }
    },

    // The app's navigation lives on ink, like the site's footer, and turns
    // green on hover / for the current page, like the site's nav menu.
    nav: {
      backgroundColor: '#19242e',           // was #FFFFFF
      color: 'rgba(255, 255, 255, 0.72)',   // was #2C3E50
      activeColor: '#adcb53',               // was #0055A4
      hoverColor: '#a0da67',                // was #3B7AA5
      fontSize: '16px',
      fontWeight: 400
    },

    table: {
      headerBackgroundColor: '#f7f7f7',
      headerTextColor: '#19242e',
      borderColor: 'rgba(0, 0, 0, 0.1)',
      rowHoverBackgroundColor: '#fbfbfb' // was a 5% tint of the primary
    }
  }
};

export default theme;
